// Agences, as Ruben picked it on 2 Nov 2026 (D-59, « 6 + 3 + 4 + 2 »), then
// reviewed the same night: the tab opens on « Livreurs » (design 3) with « À
// régler » (design 6, made compact) on top; rows open « La fiche livreur »
// (design 2); « Journée » (design 4) beside it. « Voir le compte » opens the
// agency's Compte page (design 5) with the MyGaz terms and the week.
// Reads ops_agences, ops_rider, ops_agences_day, ops_agency_statement (0051 to 0054).
import { useCallback, useEffect, useState } from 'react';
import { S } from '../lib/strings';
import { dayLabel, fmtN, xaf } from '../lib/format';
import { avgMin, firstDone, hm, laneCash, perHour } from '../lib/agences';
import type { Source } from '../lib/api';
import type { AdminStatement, Agences as Data, AgencesDay } from '../lib/types';
import { Loading } from '../components/Loading';
import { Attention } from '../components/agences/Attention';
import { AdjustCard, MarginCard, WeekTiles } from '../components/agences/AccountSide';
import { RidersTable } from '../components/agences/RidersTable';
import { RiderFile } from '../components/agences/RiderFile';
import { DayBoard } from '../components/agences/DayBoard';
import { AccountView } from '../components/agences/AccountView';
import { WeekBars } from '../components/agences/Bits';

type View = 'riders' | 'day';

function useLoad<T>(load: (() => Promise<T>) | null, deps: unknown[]) {
  const [v, setV] = useState<T | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (!load) return;
    let live = true;
    load().then((x) => { if (live) { setV(x); setErr(null); } }).catch((e) => { if (live) setErr(String(e?.message ?? e)); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return [v, err, setV] as const;
}

const Err = ({ e }: { e: string }) => <div className="card empty" style={{ marginTop: 24 }}><b>{S.errors.load}</b><span className="mono">{e}</span></div>;

export function Agences({ src, refresh, openOrder }: { src: Source; refresh: Date | null; openOrder(id: string): void }) {
  const [view, setView] = useState<View>('riders');
  const [rider, setRider] = useState<string | null>(null);
  const [account, setAccount] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [data, err] = useLoad<Data>(() => src.agences(), [src, refresh, tick]);
  const [day, dayErr] = useLoad<AgencesDay>(view === 'day' ? () => src.agencesDay() : null, [src, refresh, view, tick]);
  const [stTick, setStTick] = useState(0);
  const [st, stErr, setSt] = useLoad<AdminStatement>(account ? () => src.agencyStatement(account) : null, [src, account, tick, stTick]);
  // The account page reads again every minute while it is open and in front, and says when.
  const [stAt, setStAt] = useState<Date | null>(null);
  useEffect(() => { if (st) setStAt(new Date()); }, [st]);
  useEffect(() => {
    if (!account) return;
    const t = setInterval(() => { if (document.visibilityState === 'visible') setStTick((x) => x + 1); }, 60000);
    return () => clearInterval(t);
  }, [account]);
  const reload = useCallback(() => setTick((t) => t + 1), []);

  const go = (v: View) => { setView(v); setRider(null); setAccount(null); window.scrollTo({ top: 0 }); };
  const openRider = (id: string) => { setAccount(null); setView('riders'); setRider(id); window.scrollTo({ top: 0 }); };
  const openAccount = (id: string) => { setSt(null); setAccount(id); window.scrollTo({ top: 0 }); };

  if (err && !data) return <section className="view on"><Err e={err} /></section>;
  if (!data) return <Loading />;
  const nRiders = data.agencies.reduce((n, a) => n + a.riders.length, 0);
  const accOf = Object.fromEntries(data.agencies.map((a) => [a.agency_id, a]));
  const dispatchers = Object.fromEntries(data.agencies.map((a) => [a.agency_id, a.dispatchers?.find((d) => d.phone)?.phone ?? null]));
  const acc = account ? accOf[account] : null;
  const title = acc ? S.ag.acct.title(acc.name) : rider ? S.ag.views.riders : S.ag.views[view];
  const sub = acc ? S.ag.acct.sub : rider ? S.ag.subs.file : S.ag.subs[view];
  const today = dayLabel(new Date(), { weekday: 'long', day: 'numeric', month: 'long' });

  let body: React.ReactNode;
  if (acc) {
    body = stErr && !st ? <Err e={stErr} /> : !st ? <Loading /> : (
      <AccountView st={st} acc={{ ...st.account, name: acc.name, topup_before: st.topup_before }} admin={{ limit: st.account.limit }} updatedAt={stAt}
        side={<>
          <MarginCard balance={st.account.balance} limit={st.account.limit} feePct={st.account.fee_pct} payDays={st.account.pay_days} feePerDay={st.account.fee_per_day} />
          <WeekTiles a={acc} />
          <AdjustCard balance={st.account.balance} onAdjust={async (n, note) => { await src.adjust(acc.agency_id, n, note); reload(); }} />
        </>} />
    );
  } else if (view === 'riders') {
    body = rider ? <RiderFile data={data} riderId={rider} src={src} onBack={() => setRider(null)} onRider={setRider} openOrder={openOrder} onChanged={reload} />
      : <><Attention data={data} onRider={openRider} onAccount={openAccount} dispatchers={dispatchers} /><RidersTable data={data} onRider={openRider} onAccount={openAccount} /></>;
  } else {
    if (dayErr && !day) body = <Err e={dayErr} />;
    else if (!day) body = <Loading />;
    else {
      const lanes = day.agencies.flatMap((a) => a.riders);
      const first = firstDone(lanes), avg = avgMin(lanes);
      const onWay = lanes.filter((l) => l.open).length, installs = lanes.filter((l) => l.open?.kind === 'first_install').length;
      const carrying = lanes.filter((l) => laneCash(l) > 0).length;
      const ph = perHour(lanes);
      const fees = day.agencies.reduce((s, a) => s + a.fees, 0);
      body = (
        <>
          <div className="card ag-k4">
            <div><span className="cap">{S.ag.day.delivered}</span><b className="num">{lanes.reduce((s, l) => s + l.done.length, 0)}</b><p className="muted">{first ? S.ag.day.first(hm(first)) : S.ag.day.noneYet}</p></div>
            <div><span className="cap">{S.ag.day.onWay}</span><b className="num">{onWay}</b><p className="muted">{installs ? S.ag.day.install : '\u00a0'}</p></div>
            <div><span className="cap">{S.ag.day.cash}</span><b className="num">{fmtN(lanes.reduce((s, l) => s + laneCash(l), 0))}<small>XAF</small></b><p className="muted">{S.ag.day.cashSub(carrying)}</p></div>
            <div><span className="cap">{S.ag.day.avg}</span><b className="num">{avg != null ? <>{avg}<small>min</small></> : '—'}</b><p className="muted">{S.ag.day.avgSub}</p></div>
          </div>
          <DayBoard agencies={day.agencies} now={day.now} accounts={accOf} onRider={openRider} openOrder={openOrder} />
          <div className="ag-b2">
            <div className="card ag-pad"><h3>{S.ag.day.perHour}</h3><p className="muted ag-small">{S.ag.day.perHourSub}</p>
              <WeekBars vals={ph} labels={ph.map((_, i) => `${7 + i} h`)} w={740} h={110} today={false} /></div>
            <div className="card ag-pad"><h3>{S.ag.day.byAgency}</h3><p className="muted ag-small">{S.ag.day.byAgencySub}</p>
              {day.agencies.map((a) => {
                const ls = a.riders, n = ls.reduce((s, l) => s + l.done.length, 0);
                return <div className="ag-cl" key={a.id}><div><b>{a.name}</b><small className="muted">{S.ag.day.deliveries(n)} · {S.ag.tbl.riders(ls.filter((l) => laneCash(l) > 0).length)}</small></div><b className="num">{xaf(ls.reduce((s, l) => s + laneCash(l), 0))}</b></div>;
              })}
              <div className="ag-cl"><div><b>{S.ag.day.fees}</b><small className="muted">{S.ag.day.feesSub(data.agencies[0]?.fee_pct ?? 20)}</small></div><b className="num">{xaf(fees)}</b></div>
            </div>
          </div>
        </>
      );
    }
  }

  return (
    <section className="view on ag" aria-label={S.tabs.team}>
      <div className="ph ag-ph">
        <div>
          <div className="ag-eb">{S.ag.eyebrow(today)}</div>
          <h1>{title}</h1>
          <p>{sub}</p>
        </div>
        <div className="r">
          {acc && <button className="btn sm" onClick={() => setAccount(null)}>{S.ag.acct.back}</button>}
          <div className="seg" role="group" aria-label={S.tabs.team}>
            {(['riders', 'day'] as View[]).map((v) => (
              <button key={v} aria-pressed={!acc && view === v} onClick={() => go(v)}>
                {S.ag.views[v]}{v === 'riders' && <i className="ag-c">{nRiders}</i>}
              </button>
            ))}
          </div>
        </div>
      </div>
      {body}
    </section>
  );
}
